<template>
  <div>
    <layouts-header v-if="!authStore.user"></layouts-header>
    <user-header v-if="authStore.user"></user-header>
    <!-- Page Content -->
    <div class="content">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div class="white-bg corner-radius-10 p-4 mb-4">
              <!-- Breadcrumb -->
              <div
                class="d-flex justify-content-between align-items-center mb-4"
              >
                <h4 class="mb-0">
                  Tất cả thông báo ({{ unreadCount }} thông báo chưa đọc)
                </h4>
                <div class="d-flex align-items-center">
                  <button
                    v-if="notifications.length > 0"
                    class="btn btn-outline-primary btn-sm"
                    @click="markAllAsRead()"
                  >
                    <i class="fas fa-check-double me-1"></i>
                    Đánh dấu tất cả đã đọc
                  </button>
                </div>
              </div>
              <!-- Không có thông báo -->
              <div
                v-if="notifications.length === 0 && !loading"
                class="text-center py-5"
              >
                <i class="fa-solid fa-bell-slash text-muted fs-1 mb-3"></i>
                <p class="text-muted">Bạn không có thông báo nào</p>
              </div>

              <!-- Danh sách thông báo -->
              <div v-else class="notification-container" ref="notificationListRef">
                <div class="notification-list">
                  <div
                    v-for="(notification, index) in notifications"
                    :key="notification.documentId || index"
                    class="notification-item p-3"
                    :class="{ unread: !notification.is_read }"
                    @click="handleNotificationClick(notification)"
                  >
                    <div class="d-flex">
                      <div class="notification-icon me-3">
                        <div class="icon-circle">
                          <i
                            class="fas fa-info-circle text-info"
                          ></i>
                        </div>
                      </div>
                      <div class="notification-content">
                        <div class="fw-bold text-primary">
                          {{ notification.title }}
                        </div>
                        <div class="text-dark">{{ notification.body }}</div>
                        <div class="text-muted small mt-1">
                          {{
                            formatTime(
                              notification.createdAt
                            )
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Loading -->
                <div
                  v-if="loading"
                  class="d-flex justify-content-center align-items-center py-3"
                >
                  <a-spin />
                  <span class="ms-2">Đang tải...</span>
                </div>
                <!-- Thông báo đang tải thêm -->
                <div class="d-flex justify-content-center py-3" v-if="loading && currentPage.value > 1">
                  <a-spin size="small" />
                  <span class="ms-2">Đang tải thêm...</span>
                </div>
                
                <!-- Thông báo không còn thông báo để tải -->
                <div class="text-center text-muted py-3" v-if="!hasMoreNotifications && notifications.length > 0 && !loading">
                  <small>Bạn đã xem hết tất cả thông báo</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <layout-footer></layout-footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, defineComponent } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import {
  getNotificationsFromServer,
  markAllNotificationsAsReadFromServer,
  countUnread,
} from "@/api/strapi/notification";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/vi";
import { markNotificationAsReadFromServer } from "@/api/strapi/notification";
import { message, Pagination as APagination } from "ant-design-vue";

// Cấu hình dayjs
dayjs.extend(relativeTime);
dayjs.locale("vi");

const router = useRouter();
const authStore = useAuthStore();

// Biến trạng thái
const notifications = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);
const pageSize = ref(10);
const unreadCount = ref(0);
const hasMoreNotifications = ref(false);
const notificationListRef = ref(null);

// Lấy thông báo từ server
const fetchNotifications = async (page = 1, isLoadMore = false) => {
  try {
    loading.value = true;
    const response = await getNotificationsFromServer(page, pageSize.value);

    if (response && response.items) {
      if (isLoadMore) {
        // Nếu là tải thêm, thêm vào danh sách hiện tại
        notifications.value = [...notifications.value, ...response.items];
      } else {
        // Nếu không phải tải thêm, gán lại danh sách
        notifications.value = response.items;
      }
      
      currentPage.value = response.meta.pagination.page;
      totalPages.value = response.meta.pagination.totalPages;
      totalItems.value = response.meta.pagination.total;
      
      // Kiểm tra xem có thêm thông báo để tải không
      hasMoreNotifications.value = currentPage.value < totalPages.value;
    }
  } catch (error) {
    console.error("Lỗi khi lấy thông báo:", error);
  } finally {
    loading.value = false;
  }
};

// Tải thêm thông báo
const loadMoreNotifications = async () => {
  if (!loading.value && hasMoreNotifications.value) {
    await fetchNotifications(currentPage.value + 1, true);
  }
};

// Xử lý sự kiện cuộn
const handleScroll = () => {
  // Lấy tham chiếu đến phần tử thông báo
  const element = notificationListRef.value;
  if (!element) return;
  
  // Kiểm tra xem phần tử có hiển thị trong khung nhìn không
  const rect = element.getBoundingClientRect();
  const isVisible = rect.bottom <= window.innerHeight + 200;
  
  // Nếu phần tử hiển thị trong khung nhìn và có thể tải thêm
  if (isVisible && !loading.value && hasMoreNotifications.value) {
    loadMoreNotifications();
  }
};

// Lấy số lượng thông báo chưa đọc
const fetchUnreadCount = async () => {
  try {
    const response = await countUnread();
    if (response && response.count !== undefined) {
      unreadCount.value = response.count;
    }
  } catch (error) {
    console.error("Lỗi khi lấy số thông báo chưa đọc:", error);
  }
};

// Đánh dấu tất cả thông báo đã đọc
const markAllAsRead = async () => {
  const key = "markAllAsRead";
  try {
    message.loading({ content: "Đang đánh dấu tất cả thông báo đã đọc", key });
    await markAllNotificationsAsReadFromServer();

    // Cập nhật UI
    notifications.value = notifications.value.map((notification) => ({
      ...notification,
      is_read: true,
    }));

    // Cập nhật số thông báo chưa đọc
    unreadCount.value = 0;

    message.success({ content: "Đã đánh dấu tất cả thông báo đã đọc", key });
  } catch (error) {
    console.error("Lỗi khi đánh dấu tất cả đã đọc:", error);
    message.error({ content: "Lỗi khi đánh dấu tất cả thông báo đã đọc", key });
  }
};

const handleNotificationClick = async (notification) => {
  // Đánh dấu đã đọc nếu chưa đọc
  if (!notification.is_read) {
    try {
      await markNotificationAsReadFromServer(notification.documentId);
      notification.is_read = true;
      if (unreadCount.value > 0) {
        unreadCount.value--;
      }
    } catch (error) {
      console.error("Lỗi khi đánh dấu thông báo đã đọc:", error);
    }
  }

  // Xử lý chuyển hướng dựa trên thông báo
  if (notification.target_path) {
    // Nếu có section cần cuộn đến
    if (notification.target_section) {
      // Lưu section cần cuộn đến trước khi chuyển trang
      sessionStorage.setItem("scrollToSection", notification.target_section);
    }

    // Chuyển đến trang đích
    router.push(notification.target_path).then(() => {
      // Nếu có section cần cuộn đến, thực hiện sau khi trang đã tải xong
      if (notification.target_section) {
        // Sử dụng setTimeout để đảm bảo DOM đã được render
        setTimeout(() => {
          const element = document.getElementById(notification.target_section);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
            // Xóa section đã lưu sau khi đã cuộn
            sessionStorage.removeItem("scrollToSection");
          }
        }, 100);
      }
    });
  } else if (notification.data && notification.data.url) {
    // Fallback cho trường hợp cũ sử dụng data.url
    window.location.href = notification.data.url;
  }
};

// Xử lý khi thay đổi trang
const handlePageChange = (page) => {
  currentPage.value = page;
  fetchNotifications(page);
};

// Format thời gian
const formatTime = (time) => {
  return dayjs(time).fromNow();
};

// Lấy icon cho thông báo
const getNotificationIcon = (type) => {
  switch (type) {
    case "success":
      return "fas fa-check-circle text-success";
    case "error":
      return "fas fa-times-circle text-danger";
    case "warning":
      return "fas fa-exclamation-triangle text-warning";
    case "tournament":
      return "fas fa-trophy text-warning";
    case "match":
      return "fas fa-table-tennis text-primary";
    case "team":
      return "fas fa-users text-success";
    case "user":
      return "fas fa-user text-info";
    case "info":
    default:
      return "fas fa-info-circle text-primary";
  }
};

// Khởi tạo
onMounted(async () => {
  if (!authStore.user) {
    router.push("/login");
    return;
  }

  // Lấy thông báo và số lượng chưa đọc
  await Promise.all([fetchNotifications(1), fetchUnreadCount()]);
  
  // Thêm lắng nghe sự kiện cuộn
  window.addEventListener('scroll', handleScroll);
});

// Khi component bị hủy
onUnmounted(() => {
  // Xóa lắng nghe sự kiện cuộn
  window.removeEventListener('scroll', handleScroll);
});

</script>

<style scoped>
.notification-container {
  position: relative;
  padding-right: 5px;
}

.notification-list {
  width: 100%;
  border: 1px solid #eaeaea;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.notification-item {
  border-bottom: 1px solid #eaeaea;
  padding: 15px;
  transition: background-color 0.2s;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item:hover {
  background-color: #f9f9f9;
}

.notification-item.unread {
  background-color: #f0f7ff;
}

.notification-item.unread:hover {
  background-color: #e6f2ff;
}

.notification-content {
  margin-left: 10px;
  flex: 1;
}

.notification-time {
  font-size: 0.8rem;
  color: #999;
}

.notification-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #f5f5f5;
  margin-top: 2px;
}

.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}
</style>
